import { Activity, CheckCircle, Clock, MinusCircle, ShieldAlert, XCircle } from 'lucide-react'
import React from 'react'

import { type BrokersTask, type BrokersTaskStatus } from 'utils/web_api.schemas'

type WorkflowProgressBarProps = {
  tasks: BrokersTask[]
  className?: string
}

// Helper function to create automated task gradients
const createAutomatedGradient = (lightColor: string, mediumColor: string, darkColor: string) => ({
  background: `linear-gradient(135deg, ${lightColor}, ${mediumColor}, ${darkColor})`,
  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.3)',
})

// Helper function to create canceled task stripes
const createCanceledStripes = () => ({
  background: `repeating-linear-gradient(
    45deg,
    #64748b,
    #64748b 4px,
    transparent 4px,
    transparent 8px
  )`,
  backgroundColor: '#cbd5e1', // slate-300 as base
})

const getTaskIcon = (status: BrokersTaskStatus) => {
  switch (status) {
    case 'completed':
      return CheckCircle
    case 'pending':
      return Clock
    case 'blocked':
      return ShieldAlert
    case 'failed':
      return XCircle
    case 'excluded':
      return MinusCircle
    case 'canceled':
      return XCircle
    case 'processing':
      return Activity
    default:
      return Clock
  }
}

const getTaskProgressStyling = (
  status: BrokersTaskStatus,
  canExecute: boolean = true,
  executionMethod: string = 'manual'
): { containerClass: string; innerClass?: string; style?: React.CSSProperties } => {
  const isAutomated = executionMethod === 'automated'

  // Handle blocked pending tasks
  if (!canExecute && status === 'pending') {
    const baseStyle = isAutomated
      ? { background: 'linear-gradient(135deg, #fbbf24, #d97706, #92400e)' }
      : {}
    return {
      containerClass: 'border-2 border-amber-400 bg-transparent opacity-60 h-3',
      style: baseStyle,
    }
  }

  // Define base colors and automated styles for each status
  const statusConfig: Record<BrokersTaskStatus, {
    baseColor: string
    automatedStyle: React.CSSProperties
    manualStyle?: React.CSSProperties
  }> = {
    pending: {
      baseColor: 'bg-amber-400',
      automatedStyle: createAutomatedGradient('#fde047', '#eab308', '#a16207'),
    },
    processing: {
      baseColor: 'bg-sky-400',
      automatedStyle: createAutomatedGradient('#7dd3fc', '#0ea5e9', '#0369a1'),
    },
    blocked: {
      baseColor: 'bg-orange-500',
      automatedStyle: createAutomatedGradient('#fb923c', '#f97316', '#c2410c'),
    },
    excluded: {
      baseColor: 'bg-slate-400',
      automatedStyle: {
        ...createAutomatedGradient('#cbd5e1', '#64748b', '#334155'),
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.2)', // Slightly less highlight for gray
      },
    },
    completed: {
      baseColor: 'bg-emerald-400',
      automatedStyle: createAutomatedGradient('#6ee7b7', '#10b981', '#047857'),
    },
    failed: {
      baseColor: 'bg-rose-400',
      automatedStyle: createAutomatedGradient('#fda4af', '#f43f5e', '#be123c'),
    },
    canceled: {
      baseColor: '',
      automatedStyle: createCanceledStripes(),
      manualStyle: createCanceledStripes(),
    },
  }

  const config = statusConfig[status]

  // Handle unknown status gracefully
  if (!config) {
    return {
      containerClass: 'bg-gray-400 h-3',
      style: undefined,
    }
  }

  const customStyle = isAutomated ? config.automatedStyle : (config.manualStyle || {})

  return {
    containerClass: `${config.baseColor} h-3`,
    style: Object.keys(customStyle).length > 0 ? customStyle : undefined,
  }
}

const WorkflowProgressBar = ({
  tasks,
  className = '',
}: WorkflowProgressBarProps) => {
  if (!tasks || tasks.length === 0) {
    return <div className={`h-3 bg-gray-200 rounded-full ${className}`} />
  }

  // Sort tasks by sequence_order to match the logical flow
  const sortedTasks = [...tasks].sort(
    (a, b) => (a.sequence_order || 0) - (b.sequence_order || 0)
  )

  return (
    <div className={`flex items-end space-x-1 ${className}`}>
      {sortedTasks.map((task, _index) => {
        const styling = getTaskProgressStyling(
          task.status,
          task.can_execute,
          task.execution_method
        )
        const TaskIcon = getTaskIcon(task.status)

        return (
          <div
            key={task.id}
            className={`rounded-full flex-1 ${styling.containerClass} transition-colors duration-200 relative flex items-center justify-center`}
            style={styling.style}
            title={`${task?.task_type
                ?.replace(/Task$/, '')
                .replace(/([A-Z])/g, ' $1')
                .trim()}: ${task.status} (${task.execution_method || 'manual'})${
                task.can_execute === false ? ' (blocked by dependencies)' : ''
              }`}
          >
            {styling.innerClass
              ? (
                <div className={styling.innerClass} />
                )
              : (
                <TaskIcon className='w-3 h-3 text-white' />
                )}
          </div>
        )
      })}
    </div>
  )
}

export default WorkflowProgressBar
